import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, ChevronLeft, ChevronRight, Maximize2, Minimize2 } from 'lucide-react';

interface VideoScene {
  video: string;
  poster: string;
  title: string;
  subtitle: string;
  duration: number;
}

const scenes: VideoScene[] = [
  {
    video: 'https://videos.pexels.com/video-files/1093662/1093662-uhd_2560_1440_30fps.mp4',
    poster: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920&q=80',
    title: 'The Albanian Riviera',
    subtitle: 'Crystal waters rivaling the Maldives — without the crowds',
    duration: 6,
  },
  {
    video: 'https://videos.pexels.com/video-files/1409899/1409899-uhd_2560_1440_25fps.mp4',
    poster: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?w=1920&q=80',
    title: 'Pristine Coastlines',
    subtitle: '450km of untouched Adriatic and Ionian shores',
    duration: 6,
  },
  {
    video: 'https://videos.pexels.com/video-files/6394054/6394054-uhd_2560_1440_25fps.mp4',
    poster: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1920&q=80',
    title: 'The Accursed Mountains',
    subtitle: 'Dramatic alpine peaks and emerald valleys await the adventurous',
    duration: 6,
  },
  {
    video: 'https://videos.pexels.com/video-files/2491284/2491284-uhd_2560_1440_24fps.mp4',
    poster: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1920&q=80',
    title: 'Natural Wonders',
    subtitle: "The Blue Eye — nature's own impossibly vivid jewel",
    duration: 6,
  },
  {
    video: 'https://videos.pexels.com/video-files/4763824/4763824-uhd_2560_1440_24fps.mp4',
    poster: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1920&q=80',
    title: 'Private Villa Collection',
    subtitle: 'Handpicked estates with panoramic Mediterranean views',
    duration: 6,
  },
  {
    video: 'https://videos.pexels.com/video-files/3015510/3015510-hd_1920_1080_24fps.mp4',
    poster: 'https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=1920&q=80',
    title: 'Yacht Charters',
    subtitle: 'Cruise hidden coves and secret beaches by private vessel',
    duration: 6,
  },
  {
    video: 'https://videos.pexels.com/video-files/3571264/3571264-uhd_2560_1440_30fps.mp4',
    poster: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1920&q=80',
    title: 'Culinary Excellence',
    subtitle: 'Farm-to-table Mediterranean cuisine crafted by local artisans',
    duration: 6,
  },
  {
    video: 'https://videos.pexels.com/video-files/2169880/2169880-uhd_2560_1440_24fps.mp4',
    poster: 'https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=1920&q=80',
    title: 'Your Journey Begins',
    subtitle: 'Zera Travel — Bespoke Albanian experiences, crafted for the extraordinary',
    duration: 6,
  },
];


const TOTAL_DURATION = scenes.reduce((sum, s) => sum + s.duration, 0);

const IntroVideo: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [showOverlay, setShowOverlay] = useState(false);
  const [currentScene, setCurrentScene] = useState(0);
  const [sceneProgress, setSceneProgress] = useState(0);
  const [showText, setShowText] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoErrors, setVideoErrors] = useState<Record<number, boolean>>({});

  const containerRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sceneStartRef = useRef<number>(0);
  const transitioningRef = useRef(false);
  const currentSceneRef = useRef(0);
  const isPlayingRef = useRef(true);

  // Keep refs in sync with state
  useEffect(() => { currentSceneRef.current = currentScene; }, [currentScene]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);


  const setVideoRef = useCallback((el: HTMLVideoElement | null, index: number) => {
    videoRefs.current[index] = el;
  }, []);

  const handleVideoError = useCallback((index: number) => {
    setVideoErrors(prev => ({ ...prev, [index]: true }));
  }, []);

  const pauseAllVideos = useCallback(() => {
    videoRefs.current.forEach(v => { if (v) try { v.pause(); } catch {} });
  }, []);

  const playVideo = useCallback((index: number) => {
    const v = videoRefs.current[index];
    if (v && !videoErrors[index]) {
      v.currentTime = 0;
      v.muted = true; // ensure muted for autoplay policy
      v.play().catch(() => setVideoErrors(prev => ({ ...prev, [index]: true })));
    }
  }, [videoErrors]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  const advanceScene = useCallback(() => {
    if (transitioningRef.current) return;
    transitioningRef.current = true;
    setShowText(false);

    setTimeout(() => {
      const next = (currentSceneRef.current + 1) % scenes.length;
      pauseAllVideos();
      setCurrentScene(next);
      setSceneProgress(0);
      sceneStartRef.current = Date.now();
      setShowText(true);
      playVideo(next);
      transitioningRef.current = false;
    }, 400);
  }, [pauseAllVideos, playVideo]);

  // Main progress timer
  const startTimer = useCallback(() => {
    clearTimer();
    sceneStartRef.current = Date.now();

    timerRef.current = setInterval(() => {
      if (!isPlayingRef.current) return;
      const elapsed = (Date.now() - sceneStartRef.current) / 1000;
      const dur = scenes[currentSceneRef.current]?.duration || 6;
      const pct = Math.min((elapsed / dur) * 100, 100);
      setSceneProgress(pct);

      if (elapsed >= dur) {
        advanceScene();
      }
    }, 50);
  }, [clearTimer, advanceScene]);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    setShowOverlay(false);
    setSceneProgress(0);
    setShowText(true);
    transitioningRef.current = false;
    playVideo(currentSceneRef.current);
    startTimer();
  }, [playVideo, startTimer]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    clearTimer();
    const v = videoRefs.current[currentSceneRef.current];
    if (v) try { v.pause(); } catch {}
  }, [clearTimer]);

  const togglePlay = useCallback(() => {
    isPlaying ? handlePause() : handlePlay();
  }, [isPlaying, handlePlay, handlePause]);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const m = !prev;
      videoRefs.current.forEach(v => { if (v) v.muted = m; });
      return m;
    });
  }, []);

  const goToScene = useCallback((index: number) => {
    if (transitioningRef.current) return;
    transitioningRef.current = true;
    setShowText(false);
    clearTimer();
    pauseAllVideos();

    setTimeout(() => {
      setCurrentScene(index);
      setSceneProgress(0);
      sceneStartRef.current = Date.now();
      setShowText(true);
      if (isPlayingRef.current) {
        playVideo(index);
        startTimer();
      }
      transitioningRef.current = false;
    }, 300);
  }, [clearTimer, pauseAllVideos, playVideo, startTimer]);

  const goNext = useCallback(() => goToScene((currentScene + 1) % scenes.length), [currentScene, goToScene]);
  const goPrev = useCallback(() => goToScene((currentScene - 1 + scenes.length) % scenes.length), [currentScene, goToScene]);

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const h = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);

  useEffect(() => () => { clearTimer(); pauseAllVideos(); }, [clearTimer, pauseAllVideos]);

  // Auto-play on mount (muted autoplay is allowed by browsers)
  useEffect(() => {
    const t = setTimeout(() => {
      playVideo(0);
      startTimer();
    }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Determine which scenes to render (current, prev, next for smooth transitions)
  const visibleScenes = new Set([
    currentScene,
    (currentScene + 1) % scenes.length,
    (currentScene - 1 + scenes.length) % scenes.length,
  ]);

  const elapsed = scenes.slice(0, currentScene).reduce((s, sc) => s + sc.duration, 0) + (sceneProgress / 100) * scenes[currentScene].duration;
  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  return (
    <section className="relative bg-[#0D1B2A]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-[1px] bg-[#C9A961]" />
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">A Cinematic Journey</span>
            <div className="w-12 h-[1px] bg-[#C9A961]" />
          </div>
          <h2 className="font-serif text-3xl md:text-5xl text-white font-light mb-4">Experience Albania</h2>
          <p className="font-sans text-sm text-white/50 max-w-lg mx-auto leading-relaxed">
            A one-minute luxury film showcasing the coastlines, cities, mountains, villas, and yacht charters that define the Zera Travel experience.
          </p>
        </div>

        {/* Video Player */}
        <div
          ref={containerRef}
          className={`relative max-w-5xl mx-auto overflow-hidden group cursor-pointer bg-black ${isFullscreen ? 'w-screen h-screen max-w-none' : 'aspect-video'}`}
        >
          {/* Video / Image layers */}
          {scenes.map((scene, i) => {
            const isActive = currentScene === i;
            const shouldRender = visibleScenes.has(i) || showOverlay;
            if (!shouldRender && !isActive) return null;

            return (
              <div
                key={i}
                className="absolute inset-0 transition-opacity duration-[1200ms] ease-in-out"
                style={{ opacity: isActive ? 1 : 0, zIndex: isActive ? 1 : 0 }}
              >
                {!videoErrors[i] ? (
                  <video
                    ref={el => setVideoRef(el, i)}
                    src={scene.video}
                    poster={scene.poster}
                    muted={isMuted}
                    playsInline
                    loop
                    preload={i <= 1 ? 'auto' : 'metadata'}
                    onError={() => handleVideoError(i)}
                    className="w-full h-full object-cover"
                    style={{ filter: 'saturate(1.1) contrast(1.05) brightness(0.95)' }}
                  />
                ) : (
                  <div className="w-full h-full overflow-hidden">
                    <img
                      src={scene.poster}
                      alt={scene.title}
                      className={`w-full h-full object-cover transition-transform duration-[6000ms] ease-linear ${isPlaying && isActive ? 'scale-[1.08]' : 'scale-100'}`}
                      style={{ filter: 'saturate(1.1) contrast(1.05) brightness(0.95)' }}
                    />
                  </div>
                )}
              </div>
            );
          })}

          {/* Cinematic overlays */}
          <div className="absolute inset-0 pointer-events-none z-[2]" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, transparent 25%, transparent 75%, rgba(0,0,0,0.35) 100%)' }} />
          <div className="absolute inset-0 pointer-events-none z-[3]" style={{ boxShadow: 'inset 0 0 120px rgba(0,0,0,0.35)' }} />
          <div className={`absolute inset-0 z-[4] pointer-events-none transition-opacity duration-700 ${isPlaying ? 'bg-black/20' : 'bg-black/35'}`} />

          {/* Letterbox bars for cinematic feel */}
          <div className="absolute top-0 left-0 right-0 h-[5%] bg-black/40 pointer-events-none z-[5]" />
          <div className="absolute bottom-0 left-0 right-0 h-[5%] bg-black/40 pointer-events-none z-[5]" />

          {/* Brand watermark */}
          {!showOverlay && (
            <div className="absolute top-5 left-6 z-[8] opacity-50 pointer-events-none">
              <span className="font-serif text-sm md:text-base text-white/40 tracking-[0.35em] uppercase">Zera</span>
            </div>
          )}

          {/* Scene counter */}
          {!showOverlay && (
            <div className="absolute top-5 right-6 z-[8] pointer-events-none">
              <span className="font-mono text-[10px] text-white/35 tracking-wider">
                {String(currentScene + 1).padStart(2, '0')} / {String(scenes.length).padStart(2, '0')}
              </span>
            </div>
          )}

          {/* Text overlay */}
          {!showOverlay && (
            <div className={`absolute inset-0 z-[7] flex flex-col items-center justify-center text-center px-8 pointer-events-none transition-all duration-700 ease-out ${showText ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
              <div className={`w-8 h-[1px] bg-[#C9A961]/50 mb-5 transition-all duration-1000 delay-200 ${showText ? 'opacity-100' : 'opacity-0'}`} />
              <h3 className="font-serif text-xl sm:text-3xl md:text-4xl lg:text-[3.2rem] text-white font-light mb-3 drop-shadow-2xl tracking-wide leading-tight">
                {scenes[currentScene].title}
              </h3>
              <p className="font-sans text-[11px] sm:text-sm md:text-base text-white/75 max-w-xl drop-shadow-lg tracking-wide leading-relaxed">
                {scenes[currentScene].subtitle}
              </p>
              <div className={`w-8 h-[1px] bg-[#C9A961]/50 mt-5 transition-all duration-1000 delay-300 ${showText ? 'opacity-100' : 'opacity-0'}`} />
            </div>
          )}

          {/* Initial play overlay */}
          {showOverlay && (
            <div
              className="absolute inset-0 flex items-center justify-center z-10"
              onClick={handlePlay}
              role="button"
              tabIndex={0}
              aria-label="Play luxury film"
              onKeyDown={e => e.key === 'Enter' && handlePlay()}
            >
              <div className="absolute inset-0">
                <img
                  src={scenes[0].poster}
                  alt="Albania"
                  className="w-full h-full object-cover"
                  style={{ filter: 'saturate(1.1) brightness(0.7)' }}
                />
                <div className="absolute inset-0 bg-black/40" />
              </div>

              <div className="relative z-20 group/btn flex flex-col items-center gap-5">
                <div className="relative">
                  <div className="absolute -inset-2 w-28 h-28 md:w-32 md:h-32 rounded-full border border-white/10 animate-ping-slow" />
                  <div className="w-24 h-24 md:w-28 md:h-28 rounded-full border-2 border-white/30 flex items-center justify-center transition-all duration-500 group-hover/btn:border-[#C9A961] group-hover/btn:bg-[#C9A961]/10 group-hover/btn:scale-110 backdrop-blur-sm">
                    <Play className="w-9 h-9 md:w-11 md:h-11 text-white ml-1.5" fill="white" />
                  </div>
                </div>
                <div className="flex flex-col items-center gap-1.5">
                  <span className="font-sans text-[11px] tracking-[0.4em] uppercase text-white/80">Play Film</span>
                  <span className="font-sans text-[9px] tracking-[0.2em] text-white/40 uppercase">{fmt(TOTAL_DURATION)} — Luxury Albania Experience</span>
                </div>
              </div>
            </div>
          )}

          {/* Click to toggle */}
          {!showOverlay && (
            <div className="absolute inset-0 z-[9]" onClick={togglePlay} role="button" tabIndex={0} aria-label={isPlaying ? 'Pause' : 'Play'} onKeyDown={e => e.key === 'Enter' && togglePlay()} />
          )}

          {/* Pause indicator */}
          {!showOverlay && !isPlaying && (
            <div className="absolute inset-0 z-[10] flex items-center justify-center pointer-events-none">
              <div className="w-16 h-16 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center border border-white/10 animate-fade-in">
                <Play className="w-7 h-7 text-white ml-0.5" fill="white" />
              </div>
            </div>
          )}

          {/* Nav arrows */}
          {!showOverlay && (
            <>
              <button onClick={e => { e.stopPropagation(); goPrev(); }} className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-[11] w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-black/20 hover:bg-black/40 backdrop-blur-sm border border-white/10 hover:border-white/25 transition-all duration-300 opacity-0 group-hover:opacity-100 rounded-full" aria-label="Previous scene">
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <button onClick={e => { e.stopPropagation(); goNext(); }} className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-[11] w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-black/20 hover:bg-black/40 backdrop-blur-sm border border-white/10 hover:border-white/25 transition-all duration-300 opacity-0 group-hover:opacity-100 rounded-full" aria-label="Next scene">
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
            </>
          )}

          {/* Controls bar */}
          {!showOverlay && (
            <div className="absolute bottom-0 left-0 right-0 z-[12] bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {/* Segmented progress */}
              <div className="flex items-center gap-[2px] px-4 md:px-6 mb-1">
                {scenes.map((s, i) => (
                  <button
                    key={i}
                    onClick={e => { e.stopPropagation(); goToScene(i); if (!isPlaying) handlePlay(); }}
                    className="flex-1 h-[3px] bg-white/15 hover:bg-white/25 transition-colors relative overflow-hidden group/seg"
                    aria-label={`Scene ${i + 1}: ${s.title}`}
                  >
                    <div
                      className="absolute inset-y-0 left-0 bg-[#C9A961] transition-[width] duration-75 ease-linear"
                      style={{ width: i < currentScene ? '100%' : i === currentScene ? `${isPlaying ? sceneProgress : 0}%` : '0%' }}
                    />
                    <span className="absolute -top-7 left-1/2 -translate-x-1/2 text-[8px] text-white/60 whitespace-nowrap opacity-0 group-hover/seg:opacity-100 transition-opacity bg-black/60 px-2 py-0.5 rounded pointer-events-none">
                      {s.title}
                    </span>
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-3 md:gap-4 px-4 md:px-6 py-3 md:py-4">
                <button onClick={e => { e.stopPropagation(); togglePlay(); }} className="text-white hover:text-[#C9A961] transition-colors" aria-label={isPlaying ? 'Pause' : 'Play'}>
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" fill="white" />}
                </button>
                <button onClick={e => { e.stopPropagation(); toggleMute(); }} className="text-white hover:text-[#C9A961] transition-colors" aria-label={isMuted ? 'Unmute' : 'Mute'}>
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <span className="font-mono text-[11px] text-white/50 tabular-nums">{fmt(elapsed)} / {fmt(TOTAL_DURATION)}</span>
                <span className="font-sans text-[10px] tracking-wider text-white/35 hidden md:block ml-1 truncate max-w-[180px]">{scenes[currentScene].title}</span>
                <div className="flex-1" />
                <span className="font-sans text-[9px] tracking-[0.2em] text-white/35 uppercase hidden sm:block">Zera Travel</span>
                <button onClick={e => { e.stopPropagation(); toggleFullscreen(); }} className="text-white hover:text-[#C9A961] transition-colors" aria-label="Fullscreen">
                  {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}

          {/* Border */}
          <div className="absolute inset-0 border border-white/[0.06] pointer-events-none z-[20]" />
        </div>



        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 max-w-4xl mx-auto">
          {[
            { number: '362', label: 'Pristine Beaches' },
            { number: '3', label: 'UNESCO Sites' },
            { number: '300+', label: 'Days of Sunshine' },
            { number: '2,500+', label: 'Years of History' },
          ].map(stat => (
            <div key={stat.label} className="text-center">
              <span className="font-serif text-3xl md:text-4xl text-[#C9A961] font-light">{stat.number}</span>
              <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-white/40 mt-2">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.2); opacity: 0; }
          100% { transform: scale(1); opacity: 0; }
        }
        .animate-ping-slow { animation: ping-slow 2.5s ease-in-out infinite; }
        @keyframes fade-in {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
};

export default IntroVideo;
